import { http } from 'utils/http';
import type { SignalingTokenResponse } from 'services/signaling';
import { getSignalingSocket } from 'services/signaling';
import type {
  ParticipantDetailedResultsData,
  QuizAnswerPayload,
  QuizQuestion,
  QuizResultsData,
} from 'features/quizzes/types';

export const requestDetailedQuizResultsService = async (
  quizId: string,
  participantId: string
): Promise<ParticipantDetailedResultsData> =>
  getSignalingSocket().sendAsync<ParticipantDetailedResultsData>({
    event: 'requestDetailedQuizResults',
    data: {
      id: quizId,
      userId: participantId,
    },
  });

export const requestQuizResultsService = async (quizId: string): Promise<QuizResultsData> =>
  getSignalingSocket().sendAsync<QuizResultsData>({
    event: 'requestQuizResults',
    data: {
      id: quizId,
    },
  });

interface SubmitQuestionResponse {
  id: string;
  questionId: string;
  choices: number[];
  nextQuestion: QuizQuestion;
}

export const submitQuizQuestionService = async (
  payload: QuizAnswerPayload
): Promise<SubmitQuestionResponse> =>
  getSignalingSocket().sendAsync<SubmitQuestionResponse>({
    event: 'submitQuizQuestion',
    data: payload,
  });

export const exportQuizResultsService = async (roomId: string) => {
  const { token } = await getSignalingSocket().sendAsync<SignalingTokenResponse>({
    event: 'requestDownloadToken',
    data: {
      type: 'quizzes',
    },
  });

  return http.get<Blob>(`/room-api/${roomId}/quizzes/export?format=txt/json`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: 'blob',
  });
};

export const downloadQuizTemplateService = async (roomId: string) => {
  const { token } = await getSignalingSocket().sendAsync<SignalingTokenResponse>({
    event: 'requestDownloadToken',
    data: {
      type: 'quiz-template',
    },
  });

  return http.get<Blob>(`/room-api/${roomId}/quizzes/template`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: 'blob',
  });
};

export const exportSingleQuizService = async (roomId: string, quizId: string) => {
  const { token } = await getSignalingSocket().sendAsync<SignalingTokenResponse>({
    event: 'requestDownloadToken',
    data: {
      type: 'quizzes',
    },
  });

  return http.get<Blob>(`/room-api/${roomId}/quizzes/${quizId}/export?format=zip`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: 'blob',
  });
};

export const importQuizCsvService = async (roomId: string, file: File) => {
  const { token } = await getSignalingSocket().sendAsync<SignalingTokenResponse>({
    event: 'requestUploadToken',
    data: {
      type: 'quizzes',
    },
  });

  const formData = new FormData();
  formData.append('file', file);

  return http.post(`/room-api/${roomId}/quizzes/import`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const importQuizTemplateCsvService = async (roomId: string, file: File) => {
  const { token } = await getSignalingSocket().sendAsync<SignalingTokenResponse>({
    event: 'requestUploadToken',
    data: {
      type: 'quizzes',
    },
  });

  const formData = new FormData();
  formData.append('file', file);

  return http.post(`/room-api/${roomId}/quiz-templates/import`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
};
